import { useCashManage } from '@/hooks/shift/useCash';
import { selectCurrency } from '@/redux/config/configSlice';
import { selectShift } from '@/redux/data/dataSlice';
import { CashType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import toaster from '@/toaster';
import { Button, Form, Input, Radio, Typography } from 'antd';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

const { TextArea } = Input;
const { Title } = Typography;

interface CashFormProps {
  onFinish?: () => void;
}

const CashForm: FC<CashFormProps> = ({ onFinish }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { submit, isLoading } = useCashManage();
  const shift = useAppSelector(selectShift);
  const currency = useAppSelector(selectCurrency);
  const [type, setType] = useState<'IN' | 'OUT'>('IN');

  const handleTypeChange = (e: any) => {
    setType(e.target.value);
  };

  const handleFinish = async (values: any) => {
    if (!shift) {
      toaster.error(t('common:no_active_shift'));
      return;
    }

    const cashData: CashType = {
      ID: uuidv4(),
      value: Number(values.value),
      comment: values.comment,
      shiftId: shift.ID,
      type,
      createdAt: dayjs().toISOString(),
    };

    try {
      await submit(cashData);
      toaster.success(t('common:cash_added_successfully'));
      form.resetFields();
      if (onFinish) {
        onFinish();
      }
    } catch (error: any) {
      toaster.error(error.message || t('common:error_occurred'));
    }
  };

  return (
    <div className="p-3">
      <Title level={4} className="mb-3 text-base">
        {t('common:add_cash')}
      </Title>

      <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ type: 'IN' }}>
        <Form.Item name="type" label={t('common:transaction_type')} className="mb-3">
          <Radio.Group onChange={handleTypeChange} value={type}>
            <Radio.Button value="IN">{t('common:cash_in')}</Radio.Button>
            <Radio.Button value="OUT">{t('common:cash_out')}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="value"
          label={t('common:amount')}
          rules={[
            { required: true, message: t('common:required') },
            {
              pattern: /^[0-9]*\.?[0-9]+$/,
              message: t('common:invalid_amount'),
            },
          ]}
          className="mb-3"
        >
          <Input prefix={currency} placeholder="0.00" type="text" className="w-full" />
        </Form.Item>

        <Form.Item name="comment" label={t('common:comment')} className="mb-3">
          <TextArea rows={3} placeholder={t('common:enter_comment')} className="w-full" />
        </Form.Item>

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            className="w-full h-10"
            size="large"
          >
            {type === 'IN' ? t('common:add_cash_in') : t('common:add_cash_out')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CashForm;
